import React, { useMemo } from 'react'
import { UpdateBudgetScreen } from 'src/app/UpdateBudget/UpdateBudget'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import BudgetForm, {
  BudgetFlatFormValues,
} from 'src/components/BudgetForm/BudgetForm'
import { CreditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardListModel,
  CreditCardType,
} from 'src/models/creditCard.models'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { UserListModel } from 'src/models/user.models'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'

interface UpdateBudgetRecurringContentProps {
  creditCard: CreditCardListModel
  recipient: UserListModel
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  rcnOptions?: CreditCardOption[]
  puttingCreditCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  setScreen: (val: UpdateBudgetScreen) => void
  putCreditCard: (body: Partial<PostCreditCardRequest>) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
}

const UpdateBudgetRecurringContent: React.FC<
  UpdateBudgetRecurringContentProps
> = (props) => {
  const translate = useTranslate()

  const recurringBudgetActiveImage = useColorOrGrayscaleImage(
    ImageNames.BudgetRecurringActive,
  )
  const baseFlatFields = useMemo(
    (): BudgetFlatFormValues => ({
      displayName: props.creditCard.displayName,
      email: props.recipient.email,
      creditCardId: props.creditCard.parentCreditCardId,
      issuingLimitDollars: props.creditCard.issuingLimit,
      untilMoment: props.creditCard.untilTimezonedMoment,
      isUnlimited: props.creditCard.issuingLimitCents === undefined,
      isForever: props.creditCard.untilTimezonedMoment === undefined,
      currency: props.creditCard.currency,
      notes: props.creditCard.notes,
    }),
    [props.creditCard, props.recipient],
  )

  const initialFlatFields = useMemo(
    (): BudgetFlatFormValues => ({
      displayName: props.creditCard.displayName,
      email: props.recipient.email,
      creditCardId: props.creditCard.parentCreditCardId,
      issuingLimitDollars: props.creditCard.issuingLimit,
      untilMoment: props.creditCard.untilTimezonedMoment,
      isUnlimited: false,
      isForever: false,
      notes: props.creditCard.notes,
    }),
    [props.creditCard, props.recipient],
  )

  const baseRecurrenceFields = useMemo(
    (): Partial<RecurrenceFormValues> => ({
      amountDollars:
        props.creditCard?.recurrence?.balance || props.creditCard.issuingLimit,
      period: props.creditCard.recurrence?.period,
      interval: props.creditCard.recurrence?.interval,
      terminator: props.creditCard.recurrence?.terminator,
      count:
        props.creditCard.recurrence?.remainingCount !== undefined
          ? props.creditCard.recurrence.remainingCount
          : props.creditCard.recurrence?.count,
      untilMoment: props.creditCard.recurrence?.untilTimezonedMoment(
        props.creditCard.timezone,
      ),
      byWeekDay: props.creditCard.recurrence?.byWeekDay,
      byMonthDay: props.creditCard.recurrence?.byMonthDay,
      byYearDay: props.creditCard.recurrence?.byYearDay,
    }),
    [props.creditCard],
  )

  const initialRecurrenceFields = useMemo(
    (): Partial<RecurrenceFormValues> => ({
      amountDollars:
        props.creditCard?.recurrence?.balance || props.creditCard.issuingLimit,
      period: props.creditCard.recurrence?.period,
      interval: props.creditCard.recurrence?.interval,
      terminator: props.creditCard.recurrence?.terminator,
      count:
        props.creditCard.recurrence?.remainingCount !== undefined
          ? props.creditCard.recurrence.remainingCount
          : props.creditCard.recurrence?.count,
      untilMoment: props.creditCard.recurrence?.untilTimezonedMoment(
        props.creditCard.timezone,
      ),
      byWeekDay: props.creditCard.recurrence?.byWeekDay,
      byMonthDay: props.creditCard.recurrence?.byMonthDay,
      byYearDay: props.creditCard.recurrence?.byYearDay,
    }),
    [props.creditCard],
  )

  const onSuccessfulSubmit = (
    flatFields: BudgetFlatFormValues,
    recurrenceFields: RecurrenceFormValues,
  ): void => {
    const shouldResetCount =
      recurrenceFields.period !== props.creditCard.recurrence?.period ||
      recurrenceFields.interval !== props.creditCard.recurrence?.interval ||
      (recurrenceFields.period === RecurrencePeriod.Weekly &&
        recurrenceFields.byWeekDay !==
          props.creditCard.recurrence?.byWeekDay) ||
      (recurrenceFields.period === RecurrencePeriod.Monthly &&
        recurrenceFields.byMonthDay !==
          props.creditCard.recurrence?.byMonthDay) ||
      (recurrenceFields.period === RecurrencePeriod.Yearly &&
        recurrenceFields.byMonthDay !==
          props.creditCard.recurrence?.byYearDay) ||
      recurrenceFields.count !== props.creditCard.recurrence?.remainingCount ||
      recurrenceFields.untilMoment?.format('YYYY-MM-DD') !==
        props.creditCard.recurrence
          ?.untilTimezonedMoment(props.creditCard.timezone)
          ?.format('YYYY-MM-DD') ||
      recurrenceFields.terminator !== props.creditCard.recurrence?.terminator
    props.putCreditCard({
      creditCardId: flatFields.creditCardId,
      emails: flatFields.emails,
      displayName: flatFields.displayName,
      issuingLimitCents:
        flatFields.issuingLimitDollars !== undefined && !flatFields.isUnlimited
          ? toCents(flatFields.issuingLimitDollars)
          : undefined,
      setToUnlimited: flatFields.isUnlimited,
      type: CreditCardType.Delegate,
      recurs: true,
      recurrence: {
        balanceCents:
          recurrenceFields.amountDollars !== undefined
            ? toCents(recurrenceFields.amountDollars)
            : 0,
        period: recurrenceFields.period,
        interval: recurrenceFields.interval,
        terminator: recurrenceFields.terminator,
        count:
          recurrenceFields.terminator === RecurrenceTerminator.Count
            ? shouldResetCount
              ? recurrenceFields.count
              : props.creditCard.recurrence?.count
            : undefined,
        until:
          recurrenceFields.terminator === RecurrenceTerminator.Date
            ? recurrenceFields.untilMoment?.format('YYYY-MM-DD')
            : undefined,
        byWeekDay:
          recurrenceFields.period === RecurrencePeriod.Weekly
            ? recurrenceFields.byWeekDay
            : undefined,
        byMonthDay:
          recurrenceFields.period === RecurrencePeriod.Monthly
            ? recurrenceFields.byMonthDay
            : undefined,
        byYearDay:
          recurrenceFields.period === RecurrencePeriod.Yearly
            ? recurrenceFields.byYearDay
            : undefined,
        resetCount:
          recurrenceFields.terminator === RecurrenceTerminator.Count
            ? shouldResetCount
            : false,
      },
      reset: false,
      notes: flatFields.notes,
    })
  }

  return (
    <ActionRightFlyoutContent
      title={translate('Update Budget')}
      specificElements={{
        subtitle: translate('Recurring'),
        imageSrc: recurringBudgetActiveImage,
      }}
    >
      <BudgetForm
        topText={props.creditCard.updateFormTopText}
        buttonText={translate('Update Budget')}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.puttingCreditCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        rcnOptions={props.rcnOptions}
        userFieldLabel={translate('Recipient')}
        recurrenceEditable={true}
        initialFlatFields={initialFlatFields}
        initialRecurrenceFields={initialRecurrenceFields}
        updateProps={{
          baseFlatFields,
          baseRecurrenceFields: baseRecurrenceFields,
          timezone: props.creditCard.timezone,
        }}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        isFormDisabled={false}
        suggestedProps={{
          showSuggested: true,
          showOwnUserInSuggested: true,
        }}
      />
    </ActionRightFlyoutContent>
  )
}

export default UpdateBudgetRecurringContent
